import { onUnmounted, watch } from 'vue'
import { storeToRefs } from 'pinia'
import { useRoute } from 'vue-router'
import { useAuthenticationStore } from '@cplug/frontend-shared-domains/domain/authentication'
import useSupportStore from '../stores/useSupportStore'
import type { SupportChat } from '../models/SupportChat'
import { openOctadeskChat } from './useOctadeskChatActions'
import {
  handleOctadeskAssistantShellLayoutChange,
  handleOctadeskViewportChange,
  startWatchingAssistantSidebarLayout,
  startWatchingOctadeskPanelVisibility,
  stopWatchingAssistantSidebarLayout,
  stopWatchingOctadeskPanelVisibility
} from './useOctadeskDomLayout'
import { hasOctadeskScript, injectOctadeskScript, removeOctadeskScript } from './useOctadeskScriptLoader'
import { injectMovideskChat, isMovideskChatInjected, openMovideskChat, removeMovideskChat } from './useMovideskChat'
import { useAssistantSidebar } from '~/domain/shared/assistant/composables/useAssistantSidebar'
import './useOctadeskWindow'

export function removeSupportChat (): void {
  removeOctadeskScript()
  removeMovideskChat()
}

export function injectSupportChat (data: SupportChat): void {
  if (data.isCheffContract) {
    removeOctadeskScript()
    injectMovideskChat(data.chatCode)
    return
  }

  removeMovideskChat()
  injectOctadeskScript(data)
}

export function openSupportChat (): void {
  if (isMovideskChatInjected()) {
    openMovideskChat()
    return
  }

  openOctadeskChat()
}

export function useSupportChat (): void {
  const authStore = useAuthenticationStore()
  const { logged, companies } = storeToRefs(authStore)
  const route = useRoute()

  const supportStore = useSupportStore()
  const { isAssistantOpen, rightSidebarWidth, rightSidebarOverlay } = useAssistantSidebar()
  const isClient = import.meta.client || process.client

  if (isClient) {
    window.addEventListener('resize', handleOctadeskViewportChange)
    startWatchingAssistantSidebarLayout()
  }

  watch(
    [isAssistantOpen, rightSidebarWidth, rightSidebarOverlay],
    () => {
      handleOctadeskAssistantShellLayoutChange()
    }
  )

  watch(
    [logged, companies, (): string => route.path],
    async ([isLogged, selectedCompanies, path]) => {
      const hasCompany = selectedCompanies && selectedCompanies.length > 0
      const isAuthRoute = path.startsWith('/auth') || path.startsWith('/login')

      if (!isLogged || !hasCompany || isAuthRoute) {
        removeSupportChat()
        supportStore.$reset()
        return
      }

      if (hasOctadeskScript()) {
        startWatchingOctadeskPanelVisibility()
        return
      }

      if (isMovideskChatInjected()) {
        return
      }

      const data = await supportStore.fetch()
      if (data) {
        injectSupportChat(data)
      }
    },
    { immediate: true }
  )

  onUnmounted(() => {
    stopWatchingOctadeskPanelVisibility()
    if (isClient) {
      window.removeEventListener('resize', handleOctadeskViewportChange)
      stopWatchingAssistantSidebarLayout()
    }
  })
}
